/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomerAccount } from '@inyova/models';
import * as dayjs from 'dayjs';

import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import * as fromAppRoot from '@app/app.reducers';

import { DocType } from '@shared/models/Profile';

import { environment } from '../../../../environments/environment';
import {
  AccountCustomer,
  BankAccountDetail,
  BankDetailsCH,
  ContactDetailsCH,
  ContractQuestions,
  DocType3A,
  Employment3ADetail,
  FinancialSituation,
  FinancialSituationInvestmentProfile,
  IbanReminderResponse,
  IsoCodes,
  LifeSituationDetailsCH,
  LifeSituationQuestions,
  OddRequestData,
  PersonalDetails,
  PersonalDetailsCH,
  RegulatoryQuestions,
  RegulatoryQuestionsCH
} from '../models/Account';

@Injectable({
  providedIn: 'root'
})
export class AccountOpeningService {
  accountID: string;

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    this.store.pipe(select(fromAppRoot.selectCurrentAccount)).subscribe((value) => {
      this.accountID = value.id;
    });
  }

  getIsoCodes(): Observable<IsoCodes> {
    return this.http.get<IsoCodes>(`${environment.apiUrl}/iso-codes`);
  }

  getAccountOpeningCustomer(): Observable<AccountCustomer> {
    return this.http.get<AccountCustomer>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/customer`);
  }

  getFinancialSituation(): Observable<{ financial_detail: FinancialSituation; investment_profile: FinancialSituationInvestmentProfile }> {
    return this.http.get<{ financial_detail: FinancialSituation; investment_profile: FinancialSituationInvestmentProfile }>(
      `${environment.apiUrl}/accounts/${this.accountID}/account_opening/financial_situation`
    );
  }

  getCustomerInvestmentProfile(): Observable<FinancialSituationInvestmentProfile> {
    return this.http.get<FinancialSituationInvestmentProfile>(`${environment.apiUrl}/accounts/${this.accountID}/customer_investment_profile`);
  }

  getPersonalDetails(): Observable<PersonalDetails> {
    return this.http.get<PersonalDetails>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/personal_detail`);
  }

  getRegulatoryQuestions(): Observable<RegulatoryQuestions> {
    return this.http.get<RegulatoryQuestions>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/regulatory_detail`);
  }

  getLifeSituationQuestions(): Observable<LifeSituationQuestions> {
    return this.http.get<LifeSituationQuestions>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/life_situation`);
  }

  updateLifeSituationQuestions(body: LifeSituationQuestions): Observable<LifeSituationQuestions> {
    return this.http.put<LifeSituationQuestions>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/life_situation`, body);
  }

  generateDocuments(body: any): Observable<DocType[]> {
    return this.http.post<DocType[]>(`${environment.apiUrl}/customer_documents`, body);
  }

  updateBankTransferDetails(body: { financial_detail: Partial<FinancialSituation> }): Observable<FinancialSituation> {
    return this.http.put<FinancialSituation>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/financial_detail`, body);
  }

  updateFinancialSituation(body: {
    financial_detail: Partial<FinancialSituation>;
    investment_profile: Partial<FinancialSituationInvestmentProfile>;
    onboarding_step: number;
  }): Observable<{ financial_detail: FinancialSituation; investment_profile: FinancialSituationInvestmentProfile }> {
    return this.http.put<{ financial_detail: FinancialSituation; investment_profile: FinancialSituationInvestmentProfile }>(
      `${environment.apiUrl}/accounts/${this.accountID}/account_opening/financial_situation`,
      body
    );
  }

  updatePersonalDetails(body: { personal_detail: PersonalDetails; onboarding_step?: number }): Observable<PersonalDetails> {
    return this.http.put<PersonalDetails>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/personal_detail`, body);
  }

  updateAccountOpeningStep(body: { onboarding_step: string }): Observable<{ onboarding_step: number }> {
    return this.http.put<{ onboarding_step: number }>(`${environment.apiUrl}/accounts/${this.accountID}`, body);
  }

  update3AAgreement(): Observable<any> {
    return this.http.put<any>(`${environment.apiUrlV3}/customer_journeys/${this.accountID}`, {
      data: { id: this.accountID, type: 'customer_journey', attributes: { entered_agreement: dayjs().format() } }
    });
  }

  updateRegulatoryQuestions(body: { regulatory_detail: RegulatoryQuestions }): Observable<RegulatoryQuestions> {
    return this.http.put<RegulatoryQuestions>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/regulatory_detail`, body);
  }

  updateContractQuestions(body: ContractQuestions): Observable<ContractQuestions> {
    return this.http.put<ContractQuestions>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/contract_verification`, body);
  }

  checkCustomerIBAN(body: { iban: string }): Observable<BankAccountDetail> {
    return this.http.post<BankAccountDetail>(`${environment.apiUrl}/account_opening/iban_verification`, body);
  }

  getEmploymentDetail(): Observable<Employment3ADetail> {
    return this.http.get<Employment3ADetail>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/pillar_3a/life_situation`);
  }

  updateEmploymentDetail(body: any): Observable<Employment3ADetail> {
    return this.http.put<Employment3ADetail>(`${environment.apiUrl}/accounts/${this.accountID}/account_opening/pillar_3a/life_situation`, body);
  }

  get3AVerificationDocuments(): Observable<DocType[]> {
    return this.http.get<DocType[]>(`${environment.apiUrl}/customer_documents`);
  }

  update3AVerificationDocuments(body: DocType3A): Observable<Partial<DocType[]>> {
    return this.http.put<Partial<DocType[]>>(`${environment.apiUrl}/customer_documents`, body);
  }

  update3AAccountDataFilled(): Observable<CustomerAccount['customer_journey']> {
    return this.http.put<CustomerAccount['customer_journey']>(`${environment.apiUrl}/accounts/${this.accountID}/customer_journey`, {
      customer_journey: { account_data_filled: true }
    });
  }

  getCHPersonalDetails(): Observable<{ data: PersonalDetailsCH }> {
    return this.http.get<{ data: PersonalDetailsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/personal_detail`);
  }

  updateCHPersonalDetails(body: PersonalDetailsCH): Observable<{ data: PersonalDetailsCH }> {
    return this.http.put<{ data: PersonalDetailsCH }>(`${environment.apiUrlV3}/ch//standard/accounts/${this.accountID}/personal_detail`, {
      data: body
    });
  }

  getCHContactDetails(): Observable<{ data: ContactDetailsCH }> {
    return this.http.get<{ data: ContactDetailsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/contact_detail`);
  }

  updateCHContactDetails(body: ContactDetailsCH): Observable<{ data: ContactDetailsCH }> {
    // workarround for email. it is not editable field - delete it before send
    delete body.attributes.email;
    return this.http.put<{ data: ContactDetailsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/contact_detail`, { data: body });
  }

  getCHBankDetails(): Observable<{ data: BankDetailsCH }> {
    return this.http.get<{ data: BankDetailsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/bank_transfer`);
  }

  updateCHBankDetails(body: BankDetailsCH): Observable<{ data: BankDetailsCH }> {
    return this.http.put<{ data: BankDetailsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/bank_transfer`, { data: body });
  }

  getCHRegulatoryQuestions(): Observable<{ data: RegulatoryQuestionsCH }> {
    return this.http.get<{ data: RegulatoryQuestionsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/regulatory_detail`);
  }

  updateCHRegulatoryQuestions(body: RegulatoryQuestionsCH): Observable<{ data: RegulatoryQuestionsCH }> {
    return this.http.put<{ data: RegulatoryQuestionsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/regulatory_detail`, {
      data: body
    });
  }

  getCHLifeSituationDetails(): Observable<{ data: LifeSituationDetailsCH }> {
    return this.http.get<{ data: LifeSituationDetailsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/life_situation`);
  }

  updateCHLifeSituationDetails(body: LifeSituationDetailsCH): Observable<{ data: LifeSituationDetailsCH }> {
    return this.http.put<{ data: LifeSituationDetailsCH }>(`${environment.apiUrlV3}/ch/standard/accounts/${this.accountID}/life_situation`, {
      data: body
    });
  }

  confirmKidAccount(payload: { first_name: string; last_name: string; gender: string; dob: string }): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.apiUrl}/accounts/${this.accountID}/confirm-kid`, payload);
  }

  getIbanReminder(): Observable<IbanReminderResponse> {
    return this.http.get<IbanReminderResponse>(`${environment.apiUrlV3}/de/iban_reminders`);
  }

  getOdd(): Observable<OddRequestData> {
    return this.http.get<OddRequestData>(
      `${environment.apiUrlV3}/ch/odd/onboarding?include=personal_detail,contact_detail,life_situation,regulatory_detail`
    );
  }

  submitOdd(oddData: OddRequestData) {
    return this.http.put<OddRequestData>(`${environment.apiUrlV3}/ch/odd/onboarding`, oddData);
  }

  validateZipCode(zip: string): Observable<string[]> {
    // Todo Replace mocked data with API response when BE finishes their implementation
    let payload = [];
    if (zip === '8005') {
      payload = ['Bern', 'Zurich'];
    }
    return of(payload).pipe(delay(800));
  }

  setIbanReminder(date: string): Observable<IbanReminderResponse> {
    const data = {
      type: 'iban_reminders',
      attributes: {
        remind_at: date
      }
    };
    return this.http.put<IbanReminderResponse>(`${environment.apiUrlV3}/de/iban_reminders`, { data });
  }
}
